import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { openLink, isTMA } from "@tma.js/sdk-react";

import {
  buildScopes,
  getPermissions,
  getScopes,
  type PermissionType,
  type PermissionValue,
} from "entities/permissions";
import {
  AppPasswordSection,
  isAppPasswordValid,
  normalizeAppPassword,
} from "features/app-password";
import { useAuthRedirectLinkMutation } from "shared/api/use-auth-redirect-link-mutation";
import {
  useConnectedAccountsQueryOptions,
  type Account,
} from "shared/api/use-connected-accounts-query";
import { useEditAccountMutation } from "shared/api/use-edit-account-mutation";
import { Button } from "shared/ui/button";
import { GoogleButton } from "shared/ui/google-button";
import { Notification } from "shared/ui/notification";
import { ProgressLine } from "shared/ui/progress-line";
import { InstructionsField } from "widgets/instructions-field";
import { Permissions } from "widgets/permissions";

type Props = {
  initialInstructions: string | undefined;
  initialPermissions: PermissionType[] | undefined;
  initialScopes: string[] | undefined;
  email: string | undefined;
};

// 0: permissions, 1: google login, 2: gmail access, 3: app password, 4: instructions
const STEP_COUNT = 5;

const defaultFullAccess = (permissions: PermissionType[]) =>
  permissions.map((p) => ({
    ...p,
    value: "Full Access" as PermissionValue,
  }));

export const GoogleConnect = (props: Props) => {
  const { initialInstructions, initialPermissions, initialScopes, email } =
    props;

  // With an email in the URL the user is returning from the OAuth redirect —
  // the account already exists, so resume from the Gmail step.
  const [step, setStep] = useState(email ? 2 : 0);
  const [resolvedEmail, setResolvedEmail] = useState<string | undefined>(email);
  const [accountScopes, setAccountScopes] = useState(initialScopes ?? []);

  const [permissions, setPermissions] = useState(
    (initialPermissions
      ? initialPermissions
      : defaultFullAccess(getPermissions([], "google"))
    ).filter((permission) => permission.title !== "Gmail")
  );
  const [gmailValue, setGmailValue] = useState<PermissionValue>("Full Access");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string>();
  const [isPasswordSaved, setIsPasswordSaved] = useState(false);
  const [instructions, setInstructions] = useState(initialInstructions ?? "");

  const [isWaitingForConnection, setIsWaitingForConnection] = useState(false);
  const snapshotRef = useRef<Map<string, number> | null>(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const authRedirectLinkMutation = useAuthRedirectLinkMutation();
  const editMutation = useEditAccountMutation();

  const gmailPermission: PermissionType = {
    ...getPermissions([], "google").find((p) => p.title === "Gmail")!,
    value: gmailValue,
  };

  const accountsPollOptions = useConnectedAccountsQueryOptions({
    type: "google",
  });
  const accountsPoll = useQuery({
    ...accountsPollOptions,
    enabled: isWaitingForConnection,
    refetchInterval: isWaitingForConnection ? 2000 : false,
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    if (!isWaitingForConnection || !snapshotRef.current || !accountsPoll.data) {
      return;
    }
    const snapshot = snapshotRef.current;
    const newAccount = accountsPoll.data.connected_accounts.find(
      (acc: Account) => {
        const prev = snapshot.get(acc.name);
        return prev === undefined || acc.date_added > prev;
      }
    );
    if (newAccount) {
      snapshotRef.current = null;
      setIsWaitingForConnection(false);
      setResolvedEmail(newAccount.name);
      setAccountScopes(newAccount.scopes);
      setStep(2);
      void queryClient.invalidateQueries({
        queryKey: ["api", "connected-accounts"],
      });
    }
  }, [accountsPoll.data, isWaitingForConnection, queryClient]);

  const handlePermissionChange = (
    permission: PermissionType,
    newValue: PermissionValue
  ) => {
    setPermissions((oldValue) => {
      return oldValue.map((p) => {
        if (p.title === permission.title) {
          return { ...p, value: newValue };
        }
        return p;
      });
    });
  };

  const handleConnect = async () => {
    try {
      const scopes = getScopes(permissions, "google");
      const url = await authRedirectLinkMutation.mutateAsync({
        instructions,
        label: "",
        scopes,
      });

      const currentAccounts = await queryClient.fetchQuery(accountsPollOptions);
      snapshotRef.current = new Map(
        currentAccounts.connected_accounts.map((a) => [a.name, a.date_added])
      );
      setIsWaitingForConnection(true);

      if (import.meta.env.DEV && !isTMA()) {
        window.open(url, "_blank");
        return;
      }
      openLink(url, {
        tryInstantView: false,
      });
    } catch (error) {
      console.error(error);
      snapshotRef.current = null;
      setIsWaitingForConnection(false);
    }
  };

  const handleCancelWaiting = () => {
    snapshotRef.current = null;
    setIsWaitingForConnection(false);
  };

  const handleNext = async () => {
    if (step === 2) {
      setStep(gmailValue === "No Access" ? 4 : 3);
      return;
    }
    if (step === 3) {
      if (!resolvedEmail) return;
      try {
        setPasswordError(undefined);
        await editMutation.mutateAsync({
          type: "google",
          email: resolvedEmail,
          app_specific_password: normalizeAppPassword(password),
          scopes: buildScopes(
            [...permissions, gmailPermission],
            "google",
            accountScopes
          ),
        });
        setIsPasswordSaved(true);
        setPassword("");
        setStep(4);
      } catch (error) {
        console.error(error);
        setPasswordError("Password is not valid for this account");
      }
      return;
    }
    setStep((step) => step + 1);
  };

  const handlePrev = () => {
    if (step === 0) return navigate("/accounts");
    setStep((step) => step - 1);
  };

  const handleSkipGmail = () => {
    setGmailValue("No Access");
    setPassword("");
    setPasswordError(undefined);
    setStep(4);
  };

  // The summary step edits everything at once, so Gmail there behaves like on
  // the edit page: enabling it without a saved password requires entering one.
  const isGmailEnabled = gmailValue !== "No Access";
  const isFinishDisabled =
    isGmailEnabled && !isPasswordSaved && !isAppPasswordValid(password);

  const handleSummaryPermissionChange = (
    permission: PermissionType,
    newValue: PermissionValue
  ) => {
    if (permission.title === "Gmail") {
      setGmailValue(newValue);
      return;
    }
    handlePermissionChange(permission, newValue);
  };

  const handleFinish = async () => {
    if (!resolvedEmail) return navigate("/accounts");
    try {
      await editMutation.mutateAsync({
        type: "google",
        email: resolvedEmail,
        instructions,
        scopes: buildScopes(
          [...permissions, gmailPermission],
          "google",
          accountScopes
        ),
        app_specific_password: password
          ? normalizeAppPassword(password)
          : undefined,
      });
      navigate("/accounts");
    } catch (error) {
      console.error(error);
      if (password) {
        setPasswordError("Password is not valid for this account");
        return;
      }
      navigate("/accounts");
    }
  };

  return (
    <>
      <ProgressLine
        activeStepIndex={step}
        count={STEP_COUNT}
        className="mb-3"
      />

      {step >= 2 && resolvedEmail && (
        <h1 className="mb-5 flex text-lg leading-5.5 font-medium tracking-[0.4px] text-white">
          {resolvedEmail}
        </h1>
      )}

      {step <= 1 && (
        <Permissions
          permissions={permissions}
          onChange={handlePermissionChange}
        />
      )}

      {step === 0 && (
        <Notification className="mt-3">
          <div>
            <span className="text-clear-blue">1/5.</span> Select which tools and
            data Resonar can access for this account.
          </div>
        </Notification>
      )}

      {step === 1 && !isWaitingForConnection && (
        <>
          <Notification className="mt-3">
            <div>
              <span className="text-clear-blue">2/5.</span> Connect your Google
              account to let Resonar access your tools.
            </div>
          </Notification>
          <GoogleButton
            className="mt-2"
            onClick={handleConnect}
            isLoading={authRedirectLinkMutation.isPending}
          >
            Login with Google
          </GoogleButton>
        </>
      )}
      {step === 1 && isWaitingForConnection && (
        <>
          <Notification className="mt-3">
            <div>
              Waiting for you to finish signing in with Google in your
              browser&hellip;
            </div>
          </Notification>
          <Button
            className="mt-2"
            size="52"
            theme="secondary"
            onClick={handleCancelWaiting}
          >
            Cancel
          </Button>
        </>
      )}

      {(step === 2 || step === 3) && (
        <>
          <Permissions
            permissions={[gmailPermission]}
            onChange={(_, newValue) => setGmailValue(newValue)}
          />
          {step === 2 && (
            <Notification className="mt-3">
              <div>
                <span className="text-clear-blue">3/5.</span> To give Resonar
                access to Gmail you will need to generate an app password on
                the next step. Set Gmail to No Access to skip.
              </div>
            </Notification>
          )}
        </>
      )}

      {step === 3 && (
        <AppPasswordSection
          className="mt-5"
          description="An app password lets Resonar securely read and send your Gmail. It is not your Google account password."
          learnMoreUrl="https://support.google.com/accounts/answer/185833"
          value={password}
          error={passwordError}
          onChange={(value) => {
            setPasswordError(undefined);
            setPassword(value);
          }}
        />
      )}

      {step === 4 && (
        <>
          <Permissions
            permissions={[...permissions, gmailPermission]}
            onChange={handleSummaryPermissionChange}
          />
          {isGmailEnabled && (
            <AppPasswordSection
              className="mt-5"
              description="An app password lets Resonar securely read and send your Gmail. It is not your Google account password."
              learnMoreUrl="https://support.google.com/accounts/answer/185833"
              value={password}
              error={passwordError}
              hasSavedPassword={isPasswordSaved}
              onChange={(value) => {
                setPasswordError(undefined);
                setPassword(value);
              }}
            />
          )}
          <InstructionsField
            className="mt-5"
            instructions={instructions}
            onChange={setInstructions}
          />
        </>
      )}

      {step <= 1 && !isWaitingForConnection && (
        <div className="mt-auto flex w-full gap-x-3 pt-4">
          <Button
            size="52"
            className="w-full"
            theme="secondary"
            onClick={handlePrev}
          >
            {step === 0 ? "Cancel" : "Back"}
          </Button>
          <Button
            disabled={step === 1}
            size="52"
            className="w-full"
            theme="white"
            onClick={handleNext}
          >
            Next Step
          </Button>
        </div>
      )}

      {(step === 2 || step === 3) && (
        <div className="mt-auto flex w-full gap-x-3 pt-4">
          <Button
            size="52"
            className="w-full"
            theme="secondary"
            onClick={handleSkipGmail}
          >
            Skip Gmail
          </Button>
          <Button
            disabled={step === 3 && !isAppPasswordValid(password)}
            isLoading={editMutation.isPending}
            size="52"
            className="w-full"
            theme="white"
            onClick={handleNext}
          >
            Next Step
          </Button>
        </div>
      )}

      {step === 4 && (
        <div className="mt-auto flex w-full pt-4">
          <Button
            size="52"
            theme="white"
            className="w-full"
            disabled={isFinishDisabled}
            isLoading={editMutation.isPending}
            onClick={handleFinish}
          >
            Finish
          </Button>
        </div>
      )}
    </>
  );
};
